import { useId, useMemo, useState } from "react";
import type { Ga4CountryRow } from "./country-analytics.ts";
import {
  WORLD_COUNTRY_FEATURES,
  WORLD_MAP_HEIGHT,
  WORLD_MAP_WIDTH,
  worldCountryCentroid,
  worldCountryNumericId,
  worldCountryPath,
} from "./world-geography.ts";
import { buildWorldVisitorsModel } from "./world-visitors-model.ts";
import { useLocale } from "./localization.ts";
import "./world-visitors-map.css";

export interface WorldVisitorsMapProps {
  readonly countries: readonly Ga4CountryRow[];
}

export function WorldVisitorsMap({ countries }: WorldVisitorsMapProps) {
  const { locale, t } = useLocale();
  const titleId = useId();
  const descriptionId = useId();
  const [activeCountry, setActiveCountry] = useState<string | null>(null);
  const model = useMemo(() => buildWorldVisitorsModel(countries), [countries]);
  const countryNames = useMemo(
    () => new Intl.DisplayNames([locale], { type: "region" }),
    [locale],
  );
  const localizedCountryName = (alpha2: string, fallback: string) =>
    countryNames.of(alpha2) ?? fallback;
  const visitorLabel = (name: string, visitors: number) =>
    t("map.visitorAria", { country: name, count: visitors });
  const mappedByNumericId = useMemo(
    () => new Map(model.mapped.map((country) => [country.numeric, country])),
    [model.mapped],
  );
  const maximum = Math.max(
    1,
    ...model.mapped.map((country) => country.visitors),
  );
  const active = activeCountry
    ? (model.mapped.find((country) => country.alpha2 === activeCountry) ?? null)
    : null;
  const activeFeature = active
    ? (WORLD_COUNTRY_FEATURES.find(
        (country) => worldCountryNumericId(country) === active.numeric,
      ) ?? null)
    : null;
  const activeCentroid = activeFeature
    ? worldCountryCentroid(activeFeature)
    : null;

  return (
    <figure
      className="world-visitors-map"
      role="region"
      aria-labelledby={titleId}
    >
      <figcaption id={titleId}>{t("map.title")}</figcaption>
      <div className="world-visitors-map__viewport">
        <svg
          viewBox={`0 0 ${WORLD_MAP_WIDTH} ${WORLD_MAP_HEIGHT}`}
          role="img"
          aria-labelledby={`${titleId} ${descriptionId}`}
        >
          <desc id={descriptionId}>{t("map.description")}</desc>
          <g>
            {WORLD_COUNTRY_FEATURES.map((feature) => {
              const numeric = worldCountryNumericId(feature);
              const country = mappedByNumericId.get(numeric);
              if (!country) {
                return (
                  <path
                    key={numeric}
                    className="world-visitors-map__country"
                    d={worldCountryPath(feature)}
                    aria-hidden="true"
                  />
                );
              }
              const intensity = Math.sqrt(country.visitors / maximum);
              const isActive = activeCountry === country.alpha2;
              return (
                <path
                  key={numeric}
                  className="world-visitors-map__country world-visitors-map__country--visited"
                  d={worldCountryPath(feature)}
                  role="button"
                  tabIndex={0}
                  aria-label={visitorLabel(
                    localizedCountryName(country.alpha2, country.name),
                    country.visitors,
                  )}
                  aria-pressed={isActive}
                  data-country-code={country.alpha2}
                  data-visitors={country.visitors}
                  style={{ fillOpacity: 0.28 + intensity * 0.72 }}
                  onFocus={() => setActiveCountry(country.alpha2)}
                  onBlur={() => setActiveCountry(null)}
                  onMouseEnter={() => setActiveCountry(country.alpha2)}
                  onMouseLeave={() => setActiveCountry(null)}
                  onClick={() => setActiveCountry(country.alpha2)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setActiveCountry(country.alpha2);
                    } else if (event.key === "Escape") {
                      setActiveCountry(null);
                    }
                  }}
                />
              );
            })}
          </g>
        </svg>
        {active && activeCentroid ? (
          <div
            className="world-visitors-map__tooltip"
            role="status"
            style={{
              left: `${Math.max(8, Math.min(92, (activeCentroid[0] / WORLD_MAP_WIDTH) * 100))}%`,
              top: `${Math.max(9, Math.min(86, (activeCentroid[1] / WORLD_MAP_HEIGHT) * 100))}%`,
            }}
          >
            <strong>
              <bdi dir="auto">
                {localizedCountryName(active.alpha2, active.name)}
              </bdi>
            </strong>
            <span>{t("map.visitorCount", { count: active.visitors })}</span>
          </div>
        ) : null}
      </div>

      <div className="world-visitors-map__legend" aria-hidden="true">
        <span>{t("map.legend.lower")}</span>
        <i />
        <span>{t("map.legend.higher")}</span>
      </div>

      <details className="world-visitors-map__data" open>
        <summary>
          {t("map.allCountryData", { count: model.totalVisitors })}
        </summary>
        {model.mapped.length || model.boundaryUnavailable.length ? (
          <ol>
            {[...model.mapped, ...model.boundaryUnavailable]
              .sort(
                (left, right) =>
                  right.visitors - left.visitors ||
                  localizedCountryName(left.alpha2, left.name).localeCompare(
                    localizedCountryName(right.alpha2, right.name),
                    locale,
                  ),
              )
              .map((country) => {
                const boundaryAvailable = model.mapped.some(
                  (mapped) => mapped.alpha2 === country.alpha2,
                );
                return (
                  <li
                    key={country.alpha2}
                    data-country-code={country.alpha2}
                    data-visitors={country.visitors}
                    data-map-status={
                      boundaryAvailable ? "mapped" : "boundary-unavailable"
                    }
                  >
                    <span>
                      <bdi dir="auto">
                        {localizedCountryName(country.alpha2, country.name)}
                      </bdi>
                    </span>
                    {!boundaryAvailable ? (
                      <em>{t("map.boundaryUnavailable")}</em>
                    ) : null}
                    <strong>
                      {t("map.visitorCount", { count: country.visitors })}
                    </strong>
                  </li>
                );
              })}
          </ol>
        ) : (
          <p>{t("map.noRecognizedCountry")}</p>
        )}
      </details>

      <section
        className="world-visitors-map__unmapped"
        aria-labelledby={`${titleId}-unmapped`}
      >
        <h3 id={`${titleId}-unmapped`}>{t("map.unmappedTitle")}</h3>
        {model.unmapped.length ? (
          <ul>
            {model.unmapped.map((country) => (
              <li
                key={country.rawValue || "empty-country"}
                data-country-code={country.rawValue}
                data-visitors={country.visitors}
                data-map-status="unmapped"
              >
                <span>
                  {country.rawValue ? (
                    <bdi dir="auto">{country.rawValue}</bdi>
                  ) : (
                    t("map.emptyCountryValue")
                  )}
                </span>
                <strong>
                  {t("map.visitorCount", { count: country.visitors })}
                </strong>
              </li>
            ))}
          </ul>
        ) : (
          <p>{t("map.none")}</p>
        )}
      </section>

      <p className="visually-hidden" aria-live="polite">
        {active
          ? visitorLabel(
              localizedCountryName(active.alpha2, active.name),
              active.visitors,
            )
          : ""}
      </p>
    </figure>
  );
}
